/**
 * Certificates Management Page
 * 
 * Main page for managing certificates with features:
 * - Certificate list with pagination
 * - Create/Edit/Delete certificates
 * - Status management
 * 
 * Uses StandardPage template for consistent layout
 */

import { FC, useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FiPlus } from 'react-icons/fi';
import { certificateService } from '@/services';
import { useToast } from '@/hooks';
import StandardPage from '@/components/Common/StandardPage';
import { CertificateTable } from '@/components/CertificateTable';
import Modal from '@/components/Common/Modal';
import ConfirmDialog from '@/components/Common/ConfirmDialog';
import { CertificateForm } from '@/components/Forms/CertificateForm';
import type { CertificateListItem, CertificateFormData, CreateCertificatePayload } from '@/types';

const CertificatesPage: FC = () => {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [selectedCertificate, setSelectedCertificate] = useState<CertificateListItem | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [certificateToDelete, setCertificateToDelete] = useState<CertificateListItem | null>(null);

  const { data: certificatesData, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['certificates', pagination],
    queryFn: () =>
      certificateService.getCertificates(
        {
          page: pagination.pageIndex,
           size: pagination.pageSize,
           sort: 'createdAt,desc',

        },
        {}
      ),
    staleTime: 5 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  const createCertificateMutation = useMutation({
    mutationFn: (data: CertificateFormData & { file?: File; organizationId?: number }) => {
      if (!data.password) {
        throw new Error('Senha é obrigatória');
      }
      
      if (!data.file) {
        throw new Error('Arquivo é obrigatório');
      }

      const payload: CreateCertificatePayload = {
        file: data.file,
        metadata: {
          password: data.password,
          alias: data.alias,
          organizationId: data.organizationId || 1
        }
      };
      
      return certificateService.createCertificate(payload);
        },
        onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['certificates'] });
      setIsModalOpen(false);
      setSelectedCertificate(null);
      toast.success('Certificado criado!', 'O certificado foi criado com sucesso.');
        },
        onError: (error: Error) => {
      toast.error('Erro ao criar certificado', error.message);
    },
  });

  const updateCertificateMutation = useMutation({
    mutationFn: ({ ref, data }: { ref: string; data: Partial<CertificateFormData> }) =>
      certificateService.updateCertificate(ref, {
        alias: data.alias,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['certificates'] });
      setIsModalOpen(false);
      setSelectedCertificate(null);
      toast.success('Certificado atualizado!', 'As alterações foram salvas com sucesso.');
    },
    onError: (error: Error) => {
      toast.error('Erro ao atualizar certificado', error.message);
    },
  });

  const deleteCertificateMutation = useMutation({
    mutationFn: (ref: string) => certificateService.deleteCertificate(ref),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['certificates'] });
      toast.success('Certificado deletado!', 'O certificado foi removido com sucesso.');
    },
    onError: (error: Error) => {
      toast.error('Erro ao deletar certificado', error.message);
    },
  });

  const handleEdit = useCallback((certificate: CertificateListItem) => {
    setSelectedCertificate(certificate);
    setModalMode('edit');
    setIsModalOpen(true);
  }, []);

  const handleDelete = useCallback(
    (certificate: CertificateListItem) => {
      setCertificateToDelete(certificate);
      setIsDeleteDialogOpen(true);
    },
    []
  );

  const handleConfirmDelete = useCallback(() => {
    if (certificateToDelete) {
      deleteCertificateMutation.mutate(certificateToDelete.ref);
      setIsDeleteDialogOpen(false);
      setCertificateToDelete(null);
    }
  }, [certificateToDelete, deleteCertificateMutation]);

  const handleCancelDelete = useCallback(() => {
    setIsDeleteDialogOpen(false);
    setCertificateToDelete(null);
  }, []);

  const handleCreate = useCallback(() => {
    setSelectedCertificate(null);
    setModalMode('create');
    setIsModalOpen(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    setSelectedCertificate(null);
  }, []);

  const handleFormSubmit = useCallback(
    async (data: CertificateFormData) => {
      if (modalMode === 'create') {
        await createCertificateMutation.mutateAsync({
          ...data,
          organizationId: 1,
        });
      } else if (selectedCertificate) {
        await updateCertificateMutation.mutateAsync({
          ref: selectedCertificate.ref,
          data,
        });
      }
    },
    [modalMode, selectedCertificate, createCertificateMutation, updateCertificateMutation]
  );

  return (
    <>
      <StandardPage
        header={{
          title: 'Gerenciamento de Certificados',
          subtitle: 'Gerencie os certificados SSL/TLS do seu sistema',
          actions: [
            {
              label: 'Novo Certificado',
              icon: <FiPlus size={18} />,
              onClick: handleCreate,
              variant: 'primary',
            },
          ],
        }}
        isLoading={isLoading}
        isError={isError}
        error={error}
        onRefetch={refetch}
        pagination={{
          state: pagination,
          data: certificatesData?.pagination
            ? {
                total: certificatesData.pagination.total,
                totalPages: certificatesData.pagination.totalPages,
                page: certificatesData.pagination.page,
                pageSize: certificatesData.pagination.size,
              }
            : { total: 0, totalPages: 0, page: 0, pageSize: 10 },
          onChange: setPagination,
        }}
      >
        <CertificateTable
          certificates={certificatesData?.data || []}
          loading={false}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      </StandardPage>

      <Modal
        open={isModalOpen}
        onClose={handleModalClose}
        title={modalMode === 'create' ? 'Criar Novo Certificado' : 'Editar Certificado'}
        description={
          modalMode === 'create'
            ? 'Preencha os dados e selecione o arquivo PFX para criar um novo certificado'
            : 'Atualize as informações do certificado'
        }
        size="md"
      >
        <CertificateForm
          mode={modalMode}
          initialValues={
            selectedCertificate
              ? {
                  alias: selectedCertificate.alias,
                  password: '', // Password not stored, required for edit if needed
                  organizationId: selectedCertificate.organizationId,
                }
              : undefined
          }
          onSubmit={handleFormSubmit}
          onCancel={handleModalClose}
          isLoading={createCertificateMutation.isPending || updateCertificateMutation.isPending}
        />
      </Modal>

      <ConfirmDialog
        open={isDeleteDialogOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Deletar Certificado"
        message={`Tem certeza que deseja deletar o certificado "${certificateToDelete?.alias}"? Esta ação não pode ser desfeita.`}
        confirmText="Deletar"
        cancelText="Cancelar"
        variant="danger"
        isLoading={deleteCertificateMutation.isPending}
      />
    </>
  );
};

export default CertificatesPage;
